/**
 * Terminlink im Plan (Paul 01.10.2026).
 *
 * „Man muss dann sehen können welche Slots vom planner vorgeschlagen werden
 * in dem link!" — die drei Vorschläge stehen deshalb immer offen unter der
 * Zeile, genau so, wie sie in den Link gehen. Kanal: „wenn eine email
 * angegeben ist, erst über email, am besten mit option für beides" — beide
 * Haken stehen hier, vorbelegt vom Planer.
 *
 * 🔑 Die Schlüssel müssen zu `terminlink_senden` in
 * `services/action_handlers.py` passen.
 */
import { useEffect, useState } from "react"
import { SlotDetails, useSlotCheck } from "./SlotDetails"
import { SlotEditModal } from "./SlotEditModal"

/** Vorschlag wie im Backend (`terminlink.freie_termine`): HERO-Format,
 *  Berlin-Stunden mit „+00:00“ (KEIN echtes UTC). */
export type Vorschlag = { start?: string; end?: string; label?: string; partner_ids?: number[] }

/** „2026-10-09T07:00:00+00:00“ → „2026-10-09T07:00:00“ (Berlin, wie im Plan-Slot).
 *  🔴 Nicht über Date parsen: das „+00:00“ ist HEROs Schein-UTC. */
export const naiv = (s?: string) => (s || "").slice(0, 19)

export function dauerMin(v: Vorschlag): number {
  if (!v.start || !v.end) return 60
  const d = (Date.parse(naiv(v.end) + "Z") - Date.parse(naiv(v.start) + "Z")) / 60000
  return d > 0 ? d : 60
}

/** Plan-Slot (naive Berlin-Zeit) → Vorschlag im HERO-Format. Label leer:
 *  das Backend schreibt es beim Senden in der Form des Betriebs neu. */
export function ausSlot(start: string, dauer: number, partnerId?: number | null): Vorschlag {
  const s = naiv(start.length === 16 ? start + ":00" : start)
  const ende = new Date(Date.parse(s + "Z") + dauer * 60000).toISOString().slice(0, 19)
  return { start: s + "+00:00", end: ende + "+00:00", label: "", partner_ids: partnerId ? [partnerId] : [] }
}

const WT = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"]
export function vorschlagText(v: Vorschlag): string {
  if (v.label) return v.label
  const s = naiv(v.start)
  if (!s) return "—"
  const d = new Date(s + "Z")
  const e = new Date(Date.parse(s + "Z") + dauerMin(v) * 60000)
  const hm = (x: Date) => x.toISOString().slice(11, 16)
  return `${WT[d.getUTCDay()]}, ${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)} ${hm(d)}–${hm(e)} Uhr`
}

/** Person mit Rolle (Paul 01.10.2026): „zustimmen“ bekommt den Link,
 *  „info“ nach der Bestätigung eine Mail. Spiegel von
 *  `terminlink_personen` in services/action_handlers.py. */
export type Person = { name: string; telefon: string; email: string; rolle: "zustimmen" | "info"; quelle?: string }

export function personenAus(params: Record<string, unknown>): Person[] {
  const t = (v: unknown) => String(v ?? "").trim()
  if (Array.isArray(params.personen) && params.personen.length) {
    return (params.personen as Record<string, unknown>[]).map((q) => ({
      name: t(q.name), telefon: t(q.telefon), email: t(q.email),
      rolle: q.rolle === "info" ? "info" : "zustimmen", ...(q.quelle ? { quelle: t(q.quelle) } : {}),
    }))
  }
  const out: Person[] = []
  if (t(params.name) || t(params.telefon) || t(params.email)) {
    out.push({ name: t(params.name), telefon: t(params.telefon), email: t(params.email), rolle: "zustimmen" })
  }
  const info = params.info_an
  if (info && params.info_informieren !== false) {
    for (const a of Array.isArray(info) ? info : String(info).split(",")) {
      if (t(a)) out.push({ name: "", telefon: "", email: t(a), rolle: "info" })
    }
  }
  return out
}

/** Neue Personenliste in die Parameter schreiben — samt der alten Felder
 *  (name/telefon/email = erste Person, die zustimmt; info_an), damit Vorschau
 *  und Bestätigungsdialog dasselbe zeigen. Eine Wahl im Büro klärt die Rollen. */
export function mitPersonen(params: Record<string, unknown>, personen: Person[]): Record<string, unknown> {
  const sms = params.sms_moeglich !== false
  const zu = personen.filter((q) => q.rolle === "zustimmen")
  const erster = zu.find((q) => q.email || (q.telefon && sms)) || zu[0]
  const alt = Array.isArray(params.kanaele) ? (params.kanaele as string[]) : []
  const moeglich = (k: string) => (k === "email" ? !!erster?.email : !!erster?.telefon && sms)
  let kanaele = alt.filter(moeglich)
  if (!kanaele.length) kanaele = erster?.email ? ["email"] : erster?.telefon && sms ? ["sms"] : []
  const next: Record<string, unknown> = {
    ...params, personen, rollen_unsicher: false, kanaele,
    name: erster?.name || "", telefon: erster?.telefon || "", email: erster?.email || "",
    info_an: personen.filter((q) => q.rolle === "info" && q.email).map((q) => q.email),
  }
  delete next.info_informieren
  return next
}

/** Ein Vorschlag mit derselben Anzeige wie die Mail-Slots: Live-Check,
 *  Monteur-Wahl, Mini-Tageskalender. */
function VorschlagZeile({
  v, nr, emailId, editable, onEdit, onRemove, onMonteur,
}: {
  v: Vorschlag; nr: number; emailId: number; editable: boolean
  onEdit: () => void; onRemove: () => void; onMonteur: (pid: number) => void
}) {
  const pid = v.partner_ids?.[0] ?? null
  const check = useSlotCheck(emailId, naiv(v.start), dauerMin(v), pid)
  return (
    <li style={{ listStyle: "none", margin: "4px 0 8px" }} data-testid={`terminlink-vorschlag-${nr}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5 }}>
        <span style={{ color: "var(--text-muted)" }}>{nr}.</span>
        <span style={{ flex: 1, minWidth: 0 }}>{vorschlagText(v)}</span>
        {editable && (
          <>
            <button type="button" className="ev2-pk-item-edit" onClick={onEdit}>Bearbeiten</button>
            <button type="button" className="ev2-pk-item-edit" onClick={onRemove} aria-label={`Vorschlag ${nr} entfernen`}>Entfernen</button>
          </>
        )}
      </div>
      <div className="ev2-pk-slot-details" style={{ marginLeft: 18 }}>
        <SlotDetails
          slot={{ start_datetime: naiv(v.start), duration_minutes: dauerMin(v), partner_id: pid }}
          slotCheck={check}
          onSelectMonteur={editable ? onMonteur : undefined}
        />
      </div>
    </li>
  )
}

/** Antwort von POST /drafts/{id}/terminlink-vorschau — je Person und Weg der Text. */
type Nachricht = { name: string; weg: "sms" | "whatsapp" | "email"; an: string; betreff?: string; text: string; zeichen?: number }

/** Aufklappbar: so sieht die SMS/WhatsApp/E-Mail aus (Paul 02.11.2026, Mail
 *  139859: „dass man sie inhaltlich überprüfen kann“). Der Text kommt vom
 *  Backend — derselbe Baustein wie beim Senden; der Link ist ein Platzhalter. */
export function NachrichtVorschau({ emailId, params }: { emailId: number; params: Record<string, unknown> }) {
  const [auf, setAuf] = useState(false)
  const [daten, setDaten] = useState<{ nachrichten: Nachricht[]; hinweis: string | null } | null>(null)
  const [fehler, setFehler] = useState(false)
  const schluessel = JSON.stringify({ ...params, gesendet: undefined, vorschlaege: undefined })
  useEffect(() => {
    if (!auf) return
    const ctrl = new AbortController()
    const t = setTimeout(() => {
      setFehler(false)
      fetch(`/api/v1/drafts/${emailId}/terminlink-vorschau`, {
        method: "POST", credentials: "include", signal: ctrl.signal,
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ params: JSON.parse(schluessel) }),
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then(setDaten)
        .catch((e) => { if (!ctrl.signal.aborted) setFehler(!!e) })
    }, 300)
    return () => { clearTimeout(t); ctrl.abort() }
  }, [auf, emailId, schluessel])
  const wegText = { sms: "SMS", whatsapp: "WhatsApp", email: "E-Mail" } as const
  return (
    <div data-testid="terminlink-nachricht-vorschau">
      <button type="button" className="ev2-pk-linkbtn" aria-expanded={auf} onClick={() => setAuf((v) => !v)}
        data-testid="terminlink-nachricht-umschalten">
        {auf ? "▾" : "▸"} So sieht die Nachricht aus
      </button>
      {auf && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
          {fehler && <div style={{ fontSize: 12, color: "var(--destructive)" }}>Vorschau gerade nicht abrufbar.</div>}
          {!fehler && !daten && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Lädt …</div>}
          {daten?.hinweis && <div style={{ fontSize: 12, color: "#9A3412" }}>{daten.hinweis}</div>}
          {daten && !daten.hinweis && !daten.nachrichten.length && (
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Noch kein Empfänger mit Handynummer oder E-Mail.</div>
          )}
          {daten?.nachrichten.map((n, i) => (
            <div key={i} data-testid={`terminlink-nachricht-${i}`}
              style={{ border: "0.5px solid var(--border-color)", borderRadius: 6, padding: "6px 8px", background: "var(--bg-card)" }}>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 3 }}>
                {wegText[n.weg]} an {n.name ? `${n.name} · ` : ""}{n.an}
                {n.zeichen ? ` · ${n.zeichen} Zeichen${n.weg === "sms" && n.zeichen > 160 ? ` (${Math.ceil(n.zeichen / 153)} SMS)` : ""}` : ""}
              </div>
              {n.betreff && <div style={{ fontSize: 12.5, marginBottom: 3 }}><b>Betreff:</b> {n.betreff}</div>}
              <div style={{ fontSize: 12.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{n.text}</div>
              {n.weg === "email" && <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 3 }}>+ Signatur des Postfachs</div>}
            </div>
          ))}
          {daten?.nachrichten.length ? (
            <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>Der Link „/t/••••“ wird beim Senden durch den echten ersetzt.</div>
          ) : null}
        </div>
      )}
    </div>
  )
}

export function TerminlinkAktion({
  params, editable, onChange, emailId,
}: {
  params: Record<string, unknown>
  editable: boolean
  onChange: (next: Record<string, unknown>) => void
  /** Fuer den Live-Check der Vorschlaege (derselbe Endpunkt wie die Mail-Slots). */
  emailId: number
}) {
  const [offen, setOffen] = useState(false)
  /** Index des Vorschlags im Bearbeiten-Dialog; `neu` = hinzufuegen. */
  const [slotEdit, setSlotEdit] = useState<number | "neu" | null>(null)
  const s = (k: string) => String(params[k] ?? "").trim()
  const kanaele = Array.isArray(params.kanaele) ? (params.kanaele as string[]) : []
  const vorschlaege = (Array.isArray(params.vorschlaege) ? params.vorschlaege : []) as Vorschlag[]
  const gesendet = (params.gesendet || null) as { vorschlaege?: string[] } | null
  const personen = personenAus(params)
  const zustimmen = personen.filter((q) => q.rolle === "zustimmen")
  const unsicher = params.rollen_unsicher === true
  // Mehrere stimmen zu = Abstimmung (Stufe 2): Kanal je Person, bis zu 5 Vorschläge.
  const abstimmung = zustimmen.length > 1
  const smsOk = params.sms_moeglich !== false
  const weg = (q: Person) => (q.email ? "per E-Mail" : q.telefon && smsOk ? "per SMS" : "")

  const setzen = (patch: Record<string, unknown>) => onChange({ ...params, ...patch })
  const kanal = (k: "sms" | "email", an: boolean) => {
    const neu = an ? [...kanaele.filter((x) => x !== k), k] : kanaele.filter((x) => x !== k)
    setzen({ kanaele: (["email", "sms"] as const).filter((x) => neu.includes(x)) })
  }
  const personSetzen = (i: number, patch: Partial<Person>) =>
    onChange(mitPersonen(params, personen.map((q, j) => (j === i ? { ...q, ...patch } : q))))
  const personWeg = (i: number) => onChange(mitPersonen(params, personen.filter((_, j) => j !== i)))
  const personNeu = () => onChange(mitPersonen(params, [...personen,
    { name: "", telefon: "", email: "", rolle: zustimmen.length ? "info" : "zustimmen" }]))

  const inputCls = {
    width: "100%", fontSize: 12.5, padding: "5px 8px",
    border: "1px solid var(--border-color)", borderRadius: 6,
    background: "var(--bg-card)", color: "var(--text)", outline: "none",
  } as const
  const klein = { fontSize: 12, color: "var(--text-muted)" } as const
  const termine = gesendet?.vorschlaege?.length ? gesendet.vorschlaege : vorschlaege.map(vorschlagText)

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid="terminlink-aktion">
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={klein}>Personen</span>
        {editable && (
          <button type="button" className="ev2-pk-item-edit" style={{ marginLeft: "auto" }}
            onClick={() => setOffen((v) => !v)}>
            {offen ? "Fertig" : "Bearbeiten"}
          </button>
        )}
      </div>
      {unsicher && (
        <div style={{ fontSize: 12, color: "#9A3412", background: "#fff7ed", border: "0.5px solid #fed7aa",
          borderRadius: 5, padding: "4px 8px" }} data-testid="terminlink-rollen-unsicher">
          Bitte festlegen, wer dem Termin zustimmen muss — erst dann lässt sich der Link senden.
        </div>
      )}
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4 }}>
        {personen.map((q, i) => (
          <li key={i} data-testid={`terminlink-person-${i}`}
            style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12.5 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ flex: "1 1 auto", minWidth: 0 }}>
                <b>{q.name || (q.quelle === "absender" ? "Absender" : "Person")}</b>
                {q.telefon && <span style={klein}> · {q.telefon}</span>}
                {q.email && <span style={klein}> · {q.email}</span>}
                {q.rolle === "info" && !q.email && <span style={{ fontSize: 12, color: "#9A3412" }}> · keine E-Mail, bekommt keine Info</span>}
                {q.rolle === "zustimmen" && !weg(q) && <span style={{ fontSize: 12, color: "#9A3412" }}> · Kontakt fehlt</span>}
                {abstimmung && q.rolle === "zustimmen" && weg(q) && <span style={klein}> · Link {weg(q)}</span>}
              </span>
              <span style={{ display: "inline-flex", border: "0.5px solid var(--border-color)", borderRadius: 999, overflow: "hidden" }}>
                {(["zustimmen", "info"] as const).map((r) => (
                  <button key={r} type="button" disabled={!editable}
                    data-testid={`terminlink-rolle-${i}-${r}`}
                    onClick={() => personSetzen(i, { rolle: r })}
                    style={{
                      fontSize: 12, padding: "2px 9px", border: 0,
                      background: q.rolle === r ? "#3C3489" : "transparent",
                      color: q.rolle === r ? "#fff" : "var(--text-muted)", cursor: editable ? "pointer" : "default",
                    }}>
                    {r === "zustimmen" ? "Stimmt zu" : "Nur Info"}
                  </button>
                ))}
              </span>
            </div>
            {offen && editable && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 6 }}>
                {(["name", "telefon", "email"] as const).map((k) => (
                  <input key={k} type="text" value={q[k]} style={inputCls}
                    placeholder={k === "name" ? "Name" : k === "telefon" ? "Handynummer" : "E-Mail"}
                    onChange={(e) => personSetzen(i, { [k]: e.target.value } as Partial<Person>)}
                    data-testid={`terminlink-feld-${i}-${k}`} />
                ))}
                <button type="button" className="ev2-pk-linkbtn" onClick={() => personWeg(i)}>Entfernen</button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {offen && editable && (
        <button type="button" className="ev2-pk-linkbtn" style={{ alignSelf: "flex-start" }} onClick={personNeu}
          data-testid="terminlink-person-neu">+ Person</button>
      )}
      {abstimmung && (
        <div style={{ fontSize: 12, color: "#3C3489", background: "#EEEDFE", border: "0.5px solid rgba(60,52,137,0.3)",
          borderRadius: 5, padding: "4px 8px" }} data-testid="terminlink-abstimmung">
          Gemeinsame Abstimmung: {zustimmen.length} Personen bekommen je einen eigenen Link und kreuzen an, was passt.
          Gebucht wird ein Termin, der allen passt — nach 24 h ohne Antwort geht automatisch eine Erinnerung raus.
        </div>
      )}
      <div style={klein}>Wer „Stimmt zu“ ist, bekommt den Link; „Nur Info“ bekommt eine Mail, sobald der Termin bestätigt ist.</div>

      {!abstimmung && <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12.5 }}>
        <span style={klein}>Senden per</span>
        {(["email", "sms"] as const).map((k) => {
          // sms_moeglich=false: Betrieb hat kein SMS eingerichtet (Planer setzt es).
          const moeglich = k === "email" ? !!s("email") : !!s("telefon") && params.sms_moeglich !== false
          return (
            <label key={k} style={{ display: "inline-flex", alignItems: "center", gap: 4, opacity: moeglich ? 1 : 0.5 }}>
              <input type="checkbox" checked={kanaele.includes(k)} disabled={!editable || !moeglich}
                onChange={(e) => kanal(k, e.target.checked)} data-testid={`terminlink-kanal-${k}`} />
              {k === "email" ? "E-Mail" : "SMS"}
            </label>
          )
        })}
        {kanaele.length === 0 && (
          <span style={{ fontSize: 12, color: "var(--destructive)" }}>Bitte mindestens einen Weg wählen</span>
        )}
      </div>}

      <div>
        <div style={klein}>{gesendet ? "Gesendete Vorschläge" : "Vorschläge im Link"} (als BLOCKER reserviert, bis gewählt wird)</div>
        {gesendet ? (
          <ul style={{ margin: "2px 0 0", paddingLeft: 18, fontSize: 12.5 }} data-testid="terminlink-vorschlaege">
            {termine.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        ) : vorschlaege.length ? (
          <ul style={{ margin: "2px 0 0", padding: 0 }} data-testid="terminlink-vorschlaege">
            {vorschlaege.map((v, i) => (
              <VorschlagZeile key={`${v.start}-${i}`} v={v} nr={i + 1} emailId={emailId} editable={editable}
                onEdit={() => setSlotEdit(i)}
                onRemove={() => setzen({ vorschlaege: vorschlaege.filter((_, j) => j !== i) })}
                onMonteur={(pid) => setzen({
                  vorschlaege: vorschlaege.map((x, j) => (j === i ? { ...x, partner_ids: [pid] } : x)),
                })}
              />
            ))}
          </ul>
        ) : (
          <div style={{ fontSize: 12.5 }}>Keine Vorschau — freie Termine werden beim Senden gesucht.</div>
        )}
        {editable && !gesendet && vorschlaege.length < (abstimmung ? 5 : 3) && (
          <button type="button" className="ev2-pk-linkbtn" onClick={() => setSlotEdit("neu")}
            data-testid="terminlink-vorschlag-neu">+ Vorschlag hinzufügen</button>
        )}
      </div>
      {!gesendet && <NachrichtVorschau emailId={emailId} params={params} />}
      <SlotEditModal
        open={slotEdit !== null}
        onClose={() => setSlotEdit(null)}
        slot={typeof slotEdit === "number" && vorschlaege[slotEdit]
          ? { start_datetime: naiv(vorschlaege[slotEdit].start), duration_minutes: dauerMin(vorschlaege[slotEdit]),
              partner_id: vorschlaege[slotEdit].partner_ids?.[0] ?? null }
          : { start_datetime: "", duration_minutes: vorschlaege[0] ? dauerMin(vorschlaege[0]) : Number(params.dauer_min) || 60,
              partner_id: null }}
        label={typeof slotEdit === "number" ? `Terminlink-Vorschlag ${slotEdit + 1}` : "Neuer Terminlink-Vorschlag"}
        onSave={(next) => {
          if (!next.start_datetime) return
          const neu = ausSlot(next.start_datetime, next.duration_minutes || 60, next.partner_id)
          const liste = typeof slotEdit === "number"
            ? vorschlaege.map((x, j) => (j === slotEdit ? neu : x))
            : [...vorschlaege, neu]
          setzen({ vorschlaege: [...liste].sort((a, b) => naiv(a.start).localeCompare(naiv(b.start))) })
        }}
      />

    </div>
  )
}
